import { type ReactElement } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import { cn } from '@/lib/utils';

import type { AdminQuestionnaireTemplateViewModel } from '../api';

const EMPTY_CELL = '—';
const COMPACT_CELL_CLASS = 'h-[40px] py-0';
const COMPACT_HEAD_CLASS = 'h-[40px] py-0';
const ZEBRA_ROW_CLASS = 'even:bg-muted/55';

interface Props {
  readonly rows: readonly AdminQuestionnaireTemplateViewModel[];
  readonly onEdit: (row: AdminQuestionnaireTemplateViewModel) => void;
  readonly onDelete: (row: AdminQuestionnaireTemplateViewModel) => void;
  readonly onToggleActive: (row: AdminQuestionnaireTemplateViewModel) => void;
  readonly pendingDeleteId: string | null;
  readonly pendingUpdateId: string | null;
}

export function AdminQuestionnaireTemplatesTable({
  rows,
  onEdit,
  onDelete,
  onToggleActive,
  pendingDeleteId,
  pendingUpdateId,
}: Props): ReactElement {
  const { t } = useTranslation();
  return (
    <DataTableTable aria-label={t('routes.admin.questionnaires.templates.heading')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[40%] min-w-[10rem]">
            {t('routes.admin.questionnaires.templates.table.label')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[34%] min-w-[8rem]">
            {t('routes.admin.questionnaires.templates.table.templateKey')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[12%] min-w-[5rem]">
            {t('routes.admin.questionnaires.templates.table.active')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[14%]" align="right">
            <span className="sr-only">{t('routes.admin.questionnaires.templates.table.actions')}</span>
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {rows.map((row) => {
          const isDeleting = pendingDeleteId === row.id;
          const isSaving = pendingUpdateId === row.id;
          const editLabel = t('routes.admin.questionnaires.templates.row.editAria', { label: row.label });
          const deleteLabel = t('routes.admin.questionnaires.templates.row.deleteAria', { label: row.label });
          return (
            <DataTableTr key={row.id} className={cn(ZEBRA_ROW_CLASS, !row.active && 'opacity-60')}>
              <DataTableTd className={`${COMPACT_CELL_CLASS} text-foreground/90 truncate`} dataOffset={false}>
                {row.label.length > 0 ? row.label : EMPTY_CELL}
                {isSaving && (
                  <p className="-mb-0.5 mt-0.5 text-[10px] leading-none text-muted-foreground">
                    {t('routes.admin.questionnaires.templates.row.saving')}
                  </p>
                )}
              </DataTableTd>
              <DataTableTd
                className={`${COMPACT_CELL_CLASS} font-mono text-xs text-muted-foreground truncate`}
                dataOffset
              >
                {row.templateKey.length > 0 ? row.templateKey : EMPTY_CELL}
              </DataTableTd>
              <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
                <input
                  type="checkbox"
                  checked={row.active}
                  onChange={() => {
                    onToggleActive(row);
                  }}
                  disabled={isDeleting}
                  aria-label={t('routes.admin.questionnaires.templates.row.toggleActiveAria', {
                    label: row.label,
                  })}
                  className="h-3.5 w-3.5 cursor-pointer accent-primary"
                />
              </DataTableTd>
              <DataTableTd className={COMPACT_CELL_CLASS} align="right" dataOffset>
                <div className="inline-flex gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      onEdit(row);
                    }}
                    disabled={isDeleting}
                    aria-label={editLabel}
                    title={editLabel}
                    className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      onDelete(row);
                    }}
                    disabled={isDeleting}
                    aria-label={deleteLabel}
                    title={deleteLabel}
                    className={cn(
                      'h-6 w-6 text-muted-foreground hover:text-destructive',
                      isDeleting && 'opacity-60',
                    )}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </DataTableTd>
            </DataTableTr>
          );
        })}
      </DataTableTbody>
    </DataTableTable>
  );
}
